import styled, { createGlobalStyle, css, keyframes } from "styled-components";

/*
 * Website theme for the Home and About Us pages (Oct 2026, second redesign).
 * Palette from the approved reference: deep navy headings, bright blue accents, orange call to action,
 * and one soft tint per product pillar (blue, green, purple, orange), used the same way on both pages.
 *
 * Pillar colours (same meaning everywhere):
 *   blue    Teacher-Controlled Content / Create
 *   green   Interactive Student Engagement / Engage
 *   purple  Performance Insights / Track
 *   orange  Reuse
 *
 * One type scale for every section (desktop / phone), see TYPE below.
 * Section rhythm: 88px top and bottom (72 tablet, 56 phone), one content column that lines up with
 * the site header and footer, cards 16px radius, media 20px radius, buttons 10px radius.
 */
export const C = {
	// Text
	navy: "#142a6e",
	navyDeep: "#0b1c52",
	body: "#414b6b",
	muted: "#5f6987",
	// Accent (headline second lines, links, active menu item)
	blue: "#1f63e0",
	blueDark: "#164fc0",
	blueSoft: "#edf3ff",
	blueLine: "#d3e1fb",
	// Pillars
	green: "#16a34a",
	greenText: "#15803d",
	greenSoft: "#eaf8ef",
	greenLine: "#cbecd7",
	purple: "#7a4ee6",
	purpleSoft: "#f3eeff",
	purpleLine: "#e2d7fb",
	orange: "#f26b21",
	orangeSoft: "#fff3ea",
	orangeLine: "#fbdcc6",
	// Call to action (orange, dark enough for white text)
	cta: "linear-gradient(90deg, #e2531a 0%, #cc430c 100%)",
	ctaHover: "linear-gradient(90deg, #cf460f 0%, #b83a09 100%)",
	ctaShadow: "0 12px 24px -12px rgba(214, 72, 14, 0.75)",
	// Surfaces
	page: "#ffffff",
	tint: "#f6f8fd",
	cream: "#fff8ec",
	creamLine: "#f4e3c3",
	line: "#e3e8f3",
	gold: "#f6b51e",
	// Dark bands
	band: "linear-gradient(110deg, #0b1c52 0%, #142a6e 55%, #1b3a8f 100%)",
	onDark: "#d5def5",
	// Shadows
	cardShadow: "0 14px 36px -18px rgba(20, 42, 110, 0.22)",
	cardShadowHover: "0 22px 44px -18px rgba(20, 42, 110, 0.3)",
	mediaShadow: "0 28px 56px -28px rgba(11, 28, 82, 0.45)",
	// Motion
	ease: "cubic-bezier(0.22, 1, 0.36, 1)",
	easeSpring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
};

/** Soft tint, line and icon colour for each pillar key, so a card's colours always come in a set. */
export const TONES = {
	blue: { bg: C.blueSoft, line: C.blueLine, ink: C.blue },
	green: { bg: C.greenSoft, line: C.greenLine, ink: C.green },
	purple: { bg: C.purpleSoft, line: C.purpleLine, ink: C.purple },
	orange: { bg: C.orangeSoft, line: C.orangeLine, ink: C.orange },
};

export const floatY = keyframes`
	0%,
	100% {
		transform: translate3d(0, 0, 0);
	}

	50% {
		transform: translate3d(0, -8px, 0);
	}
`;

export const MQ = {
	// Where the site's .mygrid-container narrows from 1380px to 1320px
	CONTAINER: "@media (max-width: 1400px)",
	WIDE: "@media (max-width: 1360px)",
	STACK: "@media (max-width: 1199px)",
	TABLET: "@media (max-width: 959px)",
	SMALL: "@media (max-width: 767px)",
	PHONE: "@media (max-width: 599px)",
};

/*
 * Text roles. Every piece of text on both pages uses one of these (desktop / phone):
 *   display    52 / 38  page title (hero) only, 800        .sm_display
 *   title      36 / 28  section titles, 800                .sm_title
 *   subTitle   24 / 20  sub-section titles, 700            .sm_h3
 *   cardTitle  18 / 17  card, tile and list titles, 700    .sm_h4
 *   lead       18 / 16  intro paragraph under a title, 400 .sm_lead
 *   quote      20 / 17  testimonial quote, 500 italic
 *   body       16 / 15  paragraphs, 400                    .sm_body
 *   cardText   15 / 14  text inside cards, 400             .sm_small
 *   label      15 / 14  short emphasis lines, 600          .sm_label
 *   caption    14 / 13  roles, small tags                  .sm_caption
 *   eyebrow    13 / 12  uppercase section labels, 700      .sm_eyebrow
 *   hand       30 / 22  handwritten taglines               .sm_hand
 */
export const TYPE = {
	cardTitle: css`
		font-size: 18px;
		font-weight: 700;
		line-height: 1.3;

		${MQ.PHONE} {
			font-size: 17px;
		}
	`,
	cardText: css`
		font-size: 15px;
		font-weight: 400;
		line-height: 1.5;

		${MQ.PHONE} {
			font-size: 14px;
		}
	`,
	label: css`
		font-size: 15px;
		font-weight: 600;
		line-height: 1.35;

		${MQ.PHONE} {
			font-size: 14px;
		}
	`,
	quote: css`
		font-size: 20px;
		font-style: italic;
		font-weight: 500;
		line-height: 1.5;

		${MQ.PHONE} {
			font-size: 17px;
		}
	`,
	caption: css`
		font-size: 14px;
		font-weight: 400;
		line-height: 1.4;

		${MQ.PHONE} {
			font-size: 13px;
		}
	`,
	eyebrow: css`
		font-size: 13px;
		font-weight: 700;
		letter-spacing: 1.2px;
		line-height: 1.2;
		text-transform: uppercase;

		${MQ.PHONE} {
			font-size: 12px;
		}
	`,
	hand: css`
		font-family: "Noteworthy-Lt", "Outfit", cursive;
		font-size: 30px;
		font-weight: 700;
		line-height: 1.2;

		${MQ.PHONE} {
			font-size: 22px;
		}
	`,
};

export const ScrollRevealStyles = css`
	/* ── Scroll reveal ───────────────────────────────────────────────
	 * Elements marked with data-reveal (see Reveal.jsx) fade and rise into place the first time
	 * they enter the screen. Nothing is hidden until SitePageShell switches motion on.
	 */
	&[data-motion="on"] [data-reveal] {
		opacity: 0;
		transform: translate3d(0, 24px, 0);
		transition: opacity 0.7s ease, transform 0.8s ${C.ease};
		transition-delay: var(--reveal-delay, 0ms);
	}

	&[data-motion="on"] [data-reveal="left"] {
		transform: translate3d(-28px, 0, 0);
	}

	&[data-motion="on"] [data-reveal="right"] {
		transform: translate3d(28px, 0, 0);
	}

	&[data-motion="on"] [data-reveal="zoom"] {
		transform: translate3d(0, 12px, 0) scale(0.97);
	}

	&[data-motion="on"] [data-reveal][data-revealed="true"] {
		opacity: 1;
		transform: none;
	}

	@media (prefers-reduced-motion: reduce) {
		*,
		*::before,
		*::after {
			animation-duration: 0.01ms !important;
			animation-iteration-count: 1 !important;
			transition-duration: 0.01ms !important;
			transition-delay: 0ms !important;
		}
	}
`;

export const SitePageWrapper = styled.div`
	font-family: "Outfit", sans-serif;
	background: ${C.page};
	color: ${C.navy};
	overflow-x: clip;

	*,
	*::before,
	*::after {
		box-sizing: border-box;
	}

	h1,
	h2,
	h3,
	h4,
	h5,
	p,
	ol,
	ul,
	figure,
	blockquote {
		margin: 0;
	}

	ul,
	ol {
		padding: 0;
		list-style: none;
	}

	h1,
	h2,
	h3,
	h4 {
		font-family: "Outfit", sans-serif;
		color: ${C.navy};
		letter-spacing: 0;
	}

	p {
		font-family: "Outfit", sans-serif;
	}

	a {
		text-decoration: none;
	}

	img {
		display: block;
		max-width: 100%;
	}

	/* Same content column as the site header and footer (.mygrid-container in App.css), so every
	   section lines up with the header logo on the left and the header buttons on the right. */
	.sm_container {
		width: 100%;
		max-width: 1380px;
		margin: 0 auto;
		padding: 0 15px;

		${MQ.CONTAINER} {
			max-width: 1320px;
			padding: 0 20px;
		}
	}

	/* ── Sections ────────────────────────────────────────────────── */

	.sm_section {
		position: relative;
		padding: 88px 0;

		${MQ.TABLET} {
			padding: 72px 0;
		}

		${MQ.PHONE} {
			padding: 56px 0;
		}
	}

	.sm_section--tint {
		background: ${C.tint};
	}

	.sm_section--tight {
		padding-top: 0;
	}

	/* ── Page hero (same frame on Home and About) ────────────────── */

	.sm_hero {
		position: relative;
		overflow: hidden;
		background: #f7f9ff;
	}

	/* The photo is a panel inside the content column: its right edge is the header/footer content
	   line, and its left side fades into the page so the copy can sit over it. */
	.sm_hero_photo {
		position: absolute;
		top: 28px;
		right: 15px;
		bottom: 28px;
		width: 62%;
		overflow: hidden;
		border-radius: 24px;

		${MQ.CONTAINER} {
			right: 20px;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			object-position: center 30%;
		}

		/* Fade from the page colour into the photo, so the copy sits on a clean background */
		&::before {
			content: "";
			position: absolute;
			inset: 0;
			z-index: 1;
			background: linear-gradient(90deg, #f7f9ff 0%, rgba(247, 249, 255, 0.9) 12%, rgba(247, 249, 255, 0.35) 30%, rgba(247, 249, 255, 0) 48%);
			pointer-events: none;
		}

		/* Darker corner under the handwritten tagline, for contrast */
		&::after {
			content: "";
			position: absolute;
			inset: 40% 0 0 30%;
			z-index: 1;
			background: radial-gradient(ellipse at 85% 90%, rgba(11, 28, 82, 0.6) 0%, rgba(11, 28, 82, 0) 65%);
			pointer-events: none;
		}

		${MQ.STACK} {
			position: relative;
			top: auto;
			right: auto;
			bottom: auto;
			width: 100%;
			aspect-ratio: 2 / 1;
			border-radius: 20px;

			&::before {
				display: none;
			}
		}

		${MQ.PHONE} {
			aspect-ratio: 4 / 3;
			border-radius: 16px;
		}
	}

	.sm_hero_inner {
		position: relative;
		display: flex;
		align-items: center;
		min-height: 656px;
		padding-top: 56px;
		padding-bottom: 64px;

		${MQ.STACK} {
			flex-direction: column;
			align-items: stretch;
			gap: 32px;
			min-height: 0;
			padding-top: 24px;
			padding-bottom: 56px;
		}
	}

	.sm_hero_copy {
		position: relative;
		z-index: 2;
		display: flex;
		flex-direction: column;
		gap: 20px;
		max-width: 640px;

		${MQ.STACK} {
			order: -1;
			max-width: 720px;
		}
	}

	.sm_hero_tagline {
		position: absolute;
		right: 48px;
		bottom: 40px;
		z-index: 2;
		color: #fff;
		text-align: right;
		text-shadow: 0 2px 12px rgba(11, 28, 82, 0.55);
		transform: rotate(-5deg);

		${MQ.PHONE} {
			right: 20px;
			bottom: 24px;
		}
	}

	/* ── Type ────────────────────────────────────────────────────── */

	.sm_eyebrow {
		color: ${C.blue};
		${TYPE.eyebrow}
	}

	.sm_display,
	.sm_title,
	.sm_h3 {
		text-wrap: balance;
	}

	.sm_lead,
	.sm_body,
	.sm_small {
		text-wrap: pretty;
	}

	.sm_display {
		font-size: 52px;
		font-weight: 800;
		line-height: 1.08;
		letter-spacing: -0.5px;

		${MQ.STACK} {
			font-size: 46px;
		}

		${MQ.PHONE} {
			font-size: 38px;
		}
	}

	.sm_title {
		font-size: 36px;
		font-weight: 800;
		line-height: 1.15;
		letter-spacing: -0.3px;

		${MQ.PHONE} {
			font-size: 28px;
		}
	}

	.sm_h3 {
		font-size: 24px;
		font-weight: 700;
		line-height: 1.25;

		${MQ.PHONE} {
			font-size: 20px;
		}
	}

	.sm_h4 {
		${TYPE.cardTitle}
	}

	.sm_lead {
		color: ${C.body};
		font-size: 18px;
		font-weight: 400;
		line-height: 1.55;

		${MQ.PHONE} {
			font-size: 16px;
		}
	}

	.sm_body {
		color: ${C.body};
		font-size: 16px;
		font-weight: 400;
		line-height: 1.6;

		${MQ.PHONE} {
			font-size: 15px;
		}
	}

	.sm_small {
		color: ${C.body};
		${TYPE.cardText}
	}

	.sm_label {
		color: ${C.navy};
		${TYPE.label}
	}

	.sm_caption {
		color: ${C.muted};
		${TYPE.caption}
	}

	.sm_hand {
		${TYPE.hand}
	}

	/* Hero notes use the page font, not the handwritten face. */
	.sm_hero_tagline.sm_hand {
		font-family: "Outfit", sans-serif;
	}

	/* Gold brush underline under a handwritten line */
	.sm_hand_line {
		position: relative;
		display: inline-block;
		padding-bottom: 10px;

		&::after {
			content: "";
			position: absolute;
			right: 0;
			bottom: 0;
			width: 92%;
			height: 6px;
			border-radius: 6px;
			background: ${C.gold};
			transform: rotate(-2deg);
		}
	}

	.sm_accent {
		color: ${C.blue};
	}

	/* Section heading block: eyebrow, title, optional lead. */
	.sm_head {
		display: flex;
		flex-direction: column;
		gap: 10px;
		max-width: 820px;
	}

	.sm_head--center {
		align-items: center;
		margin: 0 auto;
		text-align: center;
	}

	/* Text on the navy bands */
	.sm_on_dark {
		.sm_eyebrow {
			color: ${C.gold};
		}

		.sm_display,
		.sm_title,
		.sm_h3,
		.sm_h4 {
			color: #fff;
		}

		.sm_accent {
			color: #8fb5ff;
		}

		.sm_lead,
		.sm_body,
		.sm_small {
			color: ${C.onDark};
		}
	}

	/* ── Buttons ─────────────────────────────────────────────────── */

	.sm_btn_row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 14px;

		${MQ.PHONE} {
			flex-direction: column;
			align-items: stretch;
		}
	}

	.sm_btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		min-height: 52px;
		padding: 14px 26px;
		border: 1.5px solid transparent;
		border-radius: 10px;
		font-family: inherit;
		font-size: 16px;
		font-weight: 700;
		line-height: 1.2;
		white-space: nowrap;
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

		&:hover {
			transform: translateY(-1px);
		}

		&:focus-visible {
			outline: 3px solid rgba(31, 99, 224, 0.45);
			outline-offset: 2px;
		}

		.sm_btn_icon {
			flex-shrink: 0;
			width: 18px;
			height: 18px;
			transition: transform 0.25s ${C.ease};
		}

		&:hover .sm_btn_icon--arrow {
			transform: translateX(3px);
		}

		${MQ.PHONE} {
			white-space: normal;
			text-align: center;
		}
	}

	.sm_btn--sm {
		min-height: 44px;
		padding: 10px 18px;
		font-size: 15px;
	}

	.sm_btn--block {
		width: 100%;
	}

	/* Primary: orange. One per section. */
	.sm_btn--primary {
		background: ${C.cta};
		box-shadow: ${C.ctaShadow};
		color: #fff;

		&:hover {
			background: ${C.ctaHover};
			color: #fff;
		}
	}

	/* Secondary: navy outline on light backgrounds. */
	.sm_btn--outline {
		border-color: ${C.navy};
		background: #fff;
		color: ${C.navy};

		&:hover {
			border-color: ${C.blue};
			background: ${C.blueSoft};
			color: ${C.blueDark};
		}
	}

	/* White button on the navy bands */
	.sm_btn--light {
		background: #fff;
		color: ${C.navy};

		&:hover {
			background: ${C.blueSoft};
			color: ${C.navy};
		}
	}

	/* Round blue play badge inside a button */
	.sm_btn_play {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 26px;
		height: 26px;
		border-radius: 50%;
		background: ${C.blue};
		color: #fff;

		svg {
			width: 12px;
			height: 12px;
			margin-left: 2px;
		}
	}

	/* ── Cards, icons ────────────────────────────────────────────── */

	.sm_card {
		position: relative;
		border: 1px solid ${C.line};
		border-radius: 16px;
		background: #fff;
	}

	.sm_card--interactive {
		transition: transform 0.35s ${C.ease}, box-shadow 0.35s ${C.ease}, border-color 0.35s ease;

		&:hover {
			transform: translateY(-4px);
			box-shadow: ${C.cardShadowHover};
		}

		&:hover .sm_icon {
			transform: scale(1.08) rotate(-4deg);
		}
	}

	/* Round icon badge; colour set by the tone class or inline style */
	.sm_icon {
		display: inline-flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		width: 56px;
		height: 56px;
		border-radius: 50%;
		transition: transform 0.4s ${C.easeSpring};

		svg {
			width: 28px;
			height: 28px;
		}

		${MQ.PHONE} {
			width: 48px;
			height: 48px;

			svg {
				width: 24px;
				height: 24px;
			}
		}
	}

	/* Tones: soft tint with coloured icon; --solid for a white icon on the colour */
	${Object.entries(TONES)
		.map(
			([tone, t]) => `
	.sm_icon--${tone} { background: ${t.bg}; color: ${t.ink}; }
	.sm_icon--${tone}.sm_icon--solid { background: ${t.ink}; color: #fff; }
	.sm_tone--${tone} { background: ${t.bg}; border-color: ${t.line}; }
	.sm_ink--${tone} { color: ${t.ink}; }`
		)
		.join("")}

	.sm_media {
		position: relative;
		overflow: hidden;
		border-radius: 20px;
		box-shadow: ${C.mediaShadow};
	}

	${ScrollRevealStyles}

`;

/*
 * Footer on these pages: the shared Footer, restyled in the page palette (light background so the
 * original logo reads, navy text, blue links). Global because the Footer keeps its own class names.
 * Removed again when the page unmounts.
 */
const RED_FILL = '[fill="#EE2D2F" i]';

export const SiteGlobalTheme = createGlobalStyle`
	.sm_site_footer .mains_footer .footer-bottom-main {
		background: ${C.page};
		border-top: 1px solid ${C.line};
	}

	.sm_site_footer .mains_footer .footer-bottom-main::before,
	.sm_site_footer .mains_footer .footer-bottom-main::after {
		display: none;
	}

	.sm_site_footer .mains_footer .footer-logo {
		width: 180px;
		max-width: 100%;
		height: auto;
	}

	.sm_site_footer .mains_footer .footer_cols h4,
	.sm_site_footer .mains_footer .footer_cols h5.social_media_title {
		margin-bottom: 14px;
		color: ${C.navy};
		font-size: 18px;
		font-weight: 700;
		letter-spacing: 0;
		line-height: 1.3;
	}

	.sm_site_footer .mains_footer .footer_cols p,
	.sm_site_footer .mains_footer .footer_cols ul li a,
	.sm_site_footer .mains_footer .footer_cols ul li span {
		color: ${C.body};
		font-size: 16px;
		font-weight: 400;
		line-height: 1.55;
	}

	.sm_site_footer .mains_footer .footer_cols ul li a:hover,
	.sm_site_footer .mains_footer .footer_cols ul li span:hover {
		color: ${C.blue} !important;
	}

	.sm_site_footer .mains_footer .copyright_footer .mygrid-container {
		border-top-color: ${C.line};
	}

	.sm_site_footer .mains_footer .copyright_txt,
	.sm_site_footer .mains_footer .credit_txt {
		color: ${C.muted};
		font-size: 14px;
		font-weight: 400;
	}

	.sm_site_footer .mains_footer .copyright_txt a {
		color: ${C.blue};
		font-weight: 600;

		&:hover {
			color: ${C.navy};
		}
	}

	.sm_site_footer .mains_footer .footer-social svg path${RED_FILL} {
		fill: ${C.blue} !important;
		transition: fill 0.2s ease;
	}

	.sm_site_footer .mains_footer .footer-social a:hover svg path${RED_FILL} {
		fill: ${C.navy} !important;
	}

	@media (max-width: 599px) {
		.sm_site_footer .mains_footer .footer_cols h4,
		.sm_site_footer .mains_footer .footer_cols h5.social_media_title {
			font-size: 16px;
		}

		.sm_site_footer .mains_footer .footer_cols p,
		.sm_site_footer .mains_footer .footer_cols ul li a,
		.sm_site_footer .mains_footer .footer_cols ul li span {
			font-size: 15px;
		}

		.sm_site_footer .mains_footer .copyright_txt,
		.sm_site_footer .mains_footer .credit_txt {
			font-size: 13px;
		}
	}
`;
